/**
 * Entitlement route guard.
 *
 * Tool routes declare the capability they need via `meta.requiresEntitlement`
 * (a single `ACCESS_KEYS.*` string or an array). When an authenticated user
 * lacks every required entitlement we don't drop them into a half-working tool
 * (whose API calls would 403) — we redirect to a graceful per-tool "locked"
 * page that explains what the tool does and offers an upgrade CTA.
 *
 * Mirrors the lazy-store pattern of `featureFlags.js`: the Pinia stores are
 * resolved INSIDE the guard (navigation time = Pinia active), so this module
 * stays import-light and the guard can be registered straight on the router.
 *
 * Ordering-independent: unauthenticated users are passed straight through so
 * the auth guard can funnel them to /login — we never hijack them into the
 * locked page.
 */
import { useAuthStore } from '@/stores/useAuthStore';
import { useEntitlementsStore } from '@/stores/useEntitlementsStore';

/**
 * The tool key drives the locked page's marketing content (icon, copy, help
 * article). Primary tool routes carry it on `meta.favoritable.id`; benchmark
 * sub-routes (`*-new` / `*-run` / `*-group`) don't, so we fall back to the
 * route name with that suffix stripped (e.g. `imbalance-benchmark-new` →
 * `imbalance-benchmark`).
 */
export function resolveToolKey(to) {
  const fromMeta = to?.meta?.favoritable?.id;
  if (fromMeta) return String(fromMeta);
  return String(to?.name || '').replace(/-(new|run|group)$/, '');
}

export async function entitlementGuard(to) {
  // The locked page itself is never gated (would loop).
  if (to.name === 'tool.locked') return true;

  // ONLY tool routes get the locked-tool redirect. Plenty of non-tool routes
  // also declare `meta.requiresEntitlement` (report dashboards like
  // `/reports/flexibility` → DATASET_FLEX, `/reports/forward`, the builder,
  // …); those must NOT be funnelled to a tool-locked marketing page — they keep
  // their own behaviour (loading with in-card restricted states where needed).
  if (!String(to.path || '').startsWith('/tools/')) return true;

  const required = to.meta?.requiresEntitlement;
  if (!required) return true;
  const keys = (Array.isArray(required) ? required : [required]).filter(Boolean);
  if (!keys.length) return true;

  // Defer unauthenticated users to the auth guard (→ /login).
  if (!useAuthStore().isAuthenticated) return true;

  const entitlements = useEntitlementsStore();
  // Make sure the granular access set is current before we decide to lock —
  // idempotent + cached, and a no-op once loaded. On failure we fall through
  // to the JWT-claim fallback baked into `canAny`.
  try {
    await entitlements.refreshAccessIfStale();
  } catch {
    // Network hiccup — rely on whatever access/claims we already have.
  }

  if (entitlements.canAny(keys)) return true;

  return {
    name: 'tool.locked',
    params: { toolKey: resolveToolKey(to) },
    query: { key: keys[0], from: to.fullPath },
  };
}
